<template>
	<JBox class="s-min-h-screen">
		<!-- Header -->
		<CLayoutEditorHeader :isLoading="isLoading" headline="Benefits" @back:click="$router.back()" />

		<!-- No benefits -->
		<JBox class="s-w-full s-mt-8 s-p-8 s-m-auto s-max-w-4xl" v-if="(!tree || !tree.length) && !isLoading">
			<JBox class="s-w-full s-p-12 s-rounded-lg s-bg-white s-shadow-lg">
				<JText class="s-text-center">{{ $t("products.thereAreCurrentlyNoBenefits") }}</JText>
				<JFlex class="s-justify-center s-mt-4">
					<JButton @click="initAddBenefit">{{ $t("products.addBenefit") }}</JButton>
				</JFlex>
			</JBox>
		</JBox>

		<JBox class="s-w-full s-max-w-3xl s-m-auto s-p-4" v-else>
			<!-- Language Switcher -->
			<JFlex class="s-w-full s-mb-2 s-py-1 s-justify-end s-items-center s-sticky s-top-0 s-bg-gray-100 ">
				<JSpinner type="dots" :delay="0" v-if="isLoading" class="s-mr-2" />
				<JBox class="s-w-3/12">
					<JSelect
						:allowSelectNothing="false"
						v-model="locale"
						@input="fetchBenefitGraph"
						:options="$store.state.settings.localesDictionary"
					/>
				</JBox>
			</JFlex>

			<!-- Benefits tree -->
			<CTree :tree="tree" :layerPadding="32" :class="{ 's-pointer-events-none': isLoading }">
				<template v-slot="{ item }">
					<JBox class="s-items-center s-bg-white s-rounded-lg s-shadow s-mb-3">
						<!-- Headline and values -->
						<JBox class="s-w-full s-px-4 s-pt-4">
							<JText class="s-w-full">{{ item.name }}</JText>
							<JText class="s-w-full s-font-mono s-text-sm s-text-gray-500">{{ item.typeId }}</JText>
							<JText class="s-w-full s-text-sm s-text-gray-500">{{ item.detailedValue }}</JText>
						</JBox>

						<!-- Options -->
						<JBox class="s-w-full s-px-4">
							<JBox class="s-mt-1">
								<JBox
									v-for="(option, j) in item.options"
									:key="`${j}-${option.optionKey}`"
									class="s-rounded s-bg-gray-100 s-border-gray-200 s-border s-mb-1 s-p-1 s-px-2"
								>
									<JFlex class="s-justify-between">
										<JBox>
											<JText class="s-text-xs s-text-gray-600">{{ option.optionKey || "---" }}</JText>
											<JText class="s-text-xs s-text-gray-600">{{ option.detailedValue }}</JText>
										</JBox>
										<JFlex class="s-pl-4 s-justify-end s-items-center">
											<JButton variant="default-light-xs" @click.stop="initUpdateBenefit(option)">
												{{ $t("products.update") }}
											</JButton>
										</JFlex>
									</JFlex>
								</JBox>

								<!-- Add Option -->
								<JBox v-if="item.optionKey || (item.options && item.options.length)">
									<JButton variant="white-xs" @click="initAddBenefitOption(item)">Add Option</JButton>
								</JBox>
							</JBox>
						</JBox>

						<!-- Benefit Action Buttons -->
						<JFlex class="s-justify-start s-w-full s-bg-gray-50  s-rounded-b-lg s-px-4 s-pt-2 s-pb-2 s-mt-4">
							<JButton
								class="s-mr-2"
								v-if="!(item.options && item.options.length)"
								variant="default-light-sm"
								@click.stop="initUpdateBenefit(item)"
							>
								{{ $t("products.update") }}
							</JButton>
							<JButton variant="white-sm" @click.stop="initAddBenefitChild({ parentTypeId: item.typeId })">
								{{ $t("products.addChildBenefit") }}
							</JButton>
						</JFlex>
					</JBox>
				</template>
			</CTree>

			<!-- Add benefit -->
			<JFlex class="s-justify-center s-mt-6 s-mb-64">
				<JButton variant="default-light-sm" @click.stop="initAddBenefit">{{ $t("products.addMainBenefit") }}</JButton>
			</JFlex>
		</JBox>

		<!-- Add Benefit modal -->
		<Portal to="layer0">
			<CModal :isVisible="benefitToCreate ? true : false">
				<JBox class="s-p-8">
					<BenefitsCreateForm v-model="benefitToCreate" />
					<JFlex class="s-justify-center">
						<JSpinner type="dots" v-if="isBenefitCreateLoading" />
					</JFlex>
					<JFlex class="s-justify-between s-mt-10">
						<JButton @click="addBenefit(benefitToCreate)">{{ $t("products.create") }}</JButton>
						<JButton variant="white" @click="benefitToCreate = null">{{ $t("products.cancel") }}</JButton>
					</JFlex>
				</JBox>
			</CModal>
		</Portal>

		<!-- Add Option modal -->
		<Portal to="layer1">
			<CModal :isVisible="benefitOptionToCreate ? true : false">
				<JBox class="s-p-8">
					<BenefitsOptionCreateForm v-model="benefitOptionToCreate" />
					<JFlex class="s-justify-center">
						<JSpinner type="dots" v-if="isBenefitCreateLoading" />
					</JFlex>
					<JFlex class="s-justify-between s-mt-2">
						<JButton @click="addBenefit(benefitOptionToCreate)">{{ $t("products.create") }}</JButton>
						<JButton variant="white" @click="benefitOptionToCreate = null">{{ $t("products.cancel") }}</JButton>
					</JFlex>
				</JBox>
			</CModal>
		</Portal>

		<!-- Update modal -->
		<Portal to="layer2">
			<CModal :isVisible="benefitToUpdate ? true : false" variant="side">
				<JFlex class="s-justify-center">
					<JSpinner type="dots" v-if="isBenefitUpdateLoading" />
				</JFlex>
				<JFlex class="s-w-full s-justify-between s-p-8">
					<JFlex>
						<JButton @click="updateBenefit">{{ $t("products.update") }}</JButton>
						<JButton variant="white" @click="benefitToUpdate = null" class="s-ml-4">{{ $t("products.cancel") }}</JButton>
					</JFlex>
					<JButton variant="error-light" @click="removeBenefit">{{ $t("products.remove") }}</JButton>
				</JFlex>
				<BenefitsDetail v-model="benefitToUpdate" :locale="locale" @dataType="initDataType" />
			</CModal>
		</Portal>

		<!-- Messaging -->
		<Portal to="layer3">
			<CMessage />
		</Portal>
	</JBox>
</template>

<script>
import CTree from "@/modules/core/components/CTree"
import BenefitsDetail from "../components/BenefitsDetail"
import BenefitsCreateForm from "../components/BenefitsCreateForm"
import BenefitsOptionCreateForm from "../components/BenefitsOptionCreateForm"
// import isObject from "lodash.isobject"

export default {
	components: { CTree, BenefitsDetail, BenefitsCreateForm, BenefitsOptionCreateForm },

	// FromRouter
	props: ["productPlan", "productType", "productVersion"],

	data() {
		return {
			locale: "en-US",
			isLoading: false,
			benefitToUpdate: null,
			benefitToCreate: null,
			benefitOptionToCreate: null,
			isBenefitCreateLoading: false,
			isBenefitUpdateLoading: false,
			dataType: null
		}
	},

	computed: {
		tree() {
			return this.$store?.state?.products?.benefitGraph
		},

		productId() {
			return {
				plan: this.productPlan,
				type: this.productType,
				version: this?.productVersion ?? null
			}
		}
	},

	methods: {
		initAddBenefit(benefit) {
			this.benefitToCreate = { typeId: benefit?.typeId, optionKey: null }
		},

		initAddBenefitChild(benefit) {
			this.benefitToCreate = { typeId: benefit?.typeId, optionKey: null, parentTypeId: benefit?.parentTypeId }
		},

		initUpdateBenefit(benefit) {
			this.benefitToUpdate = { ...benefit }
		},

		initAddBenefitOption(benefit) {
			this.benefitOptionToCreate = { typeId: benefit?.typeId, parentTypeId: benefit?.parentTypeId }
		},

		initDataType(type) {
			this.dataType = type
		},

		async fetchBenefitGraph() {
			if (this.isLoading) return

			this.isLoading = true
			const res = await this.$store.dispatch("products/fetchBenefitGraph", { productId: this.productId, locale: this.locale })

			// Handle errors
			if (res?.errors)
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: res?.errors?.[0]?.message }
				})

			this.isLoading = false
		},

		async addBenefit(payload) {
			if (this.isBenefitCreateLoading) return
			if (!payload.typeId) return
			this.isBenefitCreateLoading = true

			const res = await this.$store.dispatch("products/addBenefit", { ...payload, productId: this.productId })

			// Handle errors
			if (res?.data?.addBenefit?.status !== "success") {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: res?.data?.addBenefit?.errors?.[0] || res?.errors?.[0]?.message }
				})
				this.isBenefitCreateLoading = false
				return
			}

			if (payload.hasL10nBenefits) {
				await this.updateL10nBenefitJson(payload)
			}

			this.benefitToCreate = null
			this.benefitOptionToCreate = null
			this.isBenefitCreateLoading = false
			this.fetchBenefitGraph() // refresh state
		},

		async updateL10nBenefitJson() {
			const { isSuccess, error } = await this.$store.dispatch("products/updateL10nBenefitJson", {
				...this.benefitToCreate,
				productId: this.productId
			})

			// Handle errors
			if (!isSuccess) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: error }
				})
				this.isBenefitUpdateLoading = false
				return
			}
		},

		async updateBenefit() {
			if (this.isBenefitUpdateLoading) return
			this.isBenefitUpdateLoading = true

			const data = this.dataType === "complex" ? this.benefitToUpdate.rawData : JSON.stringify(this.benefitToUpdate.rawData)

			const { isSuccess, error } = await this.$store.dispatch("products/updateBenefit", {
				...this.benefitToUpdate,
				rawData: this.benefitToUpdate.rawData !== undefined && this.benefitToUpdate.rawData !== null ? data : null, // it wasn't working properly when we used this.benefitToUpdate?.rawData
				productId: this.productId,
				locale: this.locale
			})

			// Handle errors
			if (!isSuccess) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: error }
				})
				this.isBenefitUpdateLoading = false
				return
			}
			this.benefitToUpdate = null
			this.isBenefitUpdateLoading = false
			this.fetchBenefitGraph() // refresh state
		},

		async removeBenefit() {
			const res = await this.$store.dispatch("products/removeBenefit", {
				typeId: this.benefitToUpdate?.typeId,
				productId: this.productId,
				optionKey: this.benefitToUpdate?.optionKey
			})

			// Handle errors
			if (res?.data?.removeBenefit?.status !== "success") {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: res?.data?.removeBenefit?.errors?.[0] }
				})
				return
			}

			this.benefitToUpdate = null
			this.fetchBenefitGraph() // refresh state
		}
	},

	created() {
		this.$store.dispatch("products/resetBenefitGraph") // reset to prevent showing previous data
		this.fetchBenefitGraph()
	}
}
</script>
